import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  TextField,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
} from "@mui/material";
import { VariableSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import * as XLSX from "xlsx";
//import ReportService from "../../services/ReportService"; // Adjust import
import { useReportDataService } from "../../services/ReportDataService";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";


const columnDefs = [
    // { key: "ValidationLogId", label: "Log ID", flex: 1 },
  { key: "EmployeeID", label: "Employee ID", flex: 1 },
  { key: "FaceValidation", label: "Face", flex: 1 },
  { key: "LocatoinValidation", label: "Location", flex: 1 },
  { key: "LivelinessDetection", label: "Liveliness", flex: 1 },
  { key: "UpdatedByUserName", label: "Updated By", flex: 2 },
  { key: "UpdatedOnFormatted", label: "Updated On", flex: 2 },
];

const ROW_HEIGHT = 32;

const ValidationLogList = ({EmpID = "All"}) => {
  const { fetchEmployeeValidationDataLogs } = useReportDataService();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState({});
  const [loading, setLoading] = useState(true);
  const listRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    fetchLogs();
  }, [EmpID]);

  const removeDuplicates = (data) => {
  const seen = new Set();
  return data.filter((item) => {
    const serialized = JSON.stringify(item);
    if (seen.has(serialized)) return false;
    seen.add(serialized);
    return true;
  });
};


  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await fetchEmployeeValidationDataLogs({ employeeID: EmpID || "All" },false); // Adjust function
      const cleaned = removeDuplicates(res); 
      const filtered = EmpID === "All"
        ? cleaned
        : cleaned.filter(item => item.EmployeeID === EmpID);
      const sorted = [...filtered].sort((a, b) =>
        new Date(b.UpdatedOnFormatted) - new Date(a.UpdatedOnFormatted)
      );      
      setData(sorted);
    } catch (err) {
      console.error("Failed to fetch validation logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    return data.filter((item) => 
      columnDefs.every(({ key }) => {
        const term = (search[key] || "").toLowerCase();
        if (!term) return true;
        const val = item[key];
        return val !== null &&
          val !== undefined &&
          val.toString().toLowerCase().includes(term);
      })
    );
  }, [data, search]);

  const Row = ({ index, style }) => {
    const row = filteredData[index];

  const adjustedStyle = {
    ...style,
    top: Math.floor(style.top), // 👈 round down to nearest pixel
  };

    return (
      <Box
        style={adjustedStyle}
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #eee",
          px: 0.8,
          fontSize: "0.875rem",
        }}
      >
        {columnDefs.map(({ key, flex }) => {
          const rawValue = row[key];
          //const display = val === null || val === undefined ? "-" : String(val); 
          const display = rawValue !== null && rawValue !== undefined ? String(rawValue) : "-";
           
          return (
            <Box
              key={key}
              sx={{
                flex,
                minWidth: 0,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                pr: 1,
              }}
            >
              {["Yes", "No"].includes(display) ? (
                <Chip
                  label={display}
                  size="small"
                  color={display === "Yes" ? "success" : "error"}
                />
              ) : (
                key === "UpdatedOnFormatted" ? (
                <>{dayjs(row[key]).format("MMM DD, YYYY hh:mm A")}</>
                ) : (
                display
                )
              )}
            </Box>
          );
        })}

        {/* <Box sx={{ width: 32 }}>
          <IconButton size="small">
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box> */}
      </Box>
    );
  };

  const handleBackClick = () => {    
    navigate("/ValidationRequirementsReport");
  };

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ValidationLogs");
    XLSX.writeFile(wb, "ValidationLogs.xlsx");
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography variant={EmpID === "All" ? "h2" : "h6" }>Validation Requirements Log</Typography>
        {EmpID === "All" && (
         <Box display="flex" gap={1}>                 
                    <Button variant="contained" onClick={handleExport}>
                      Export to Excel
                    </Button>
                     <Button variant="contained" onClick={handleBackClick}>
                      Back
                    </Button>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 1 }} />

      {loading ? (
        <LinearProgress />
      ) : (
        <Paper>
          {/* Search Header */}
          <Box sx={{ display: "flex", p: 1, borderBottom: "1px solid #ccc" }}>
            {columnDefs.map(({ key, label, flex }) => (
              <Box key={key} sx={{ flex, pr: 1, minWidth: 0 }}>
                <TextField
                  label={label}
                  variant="standard"
                  value={search[key] || ""}
                  onChange={(e) =>
                    setSearch((prev) => ({ ...prev, [key]: e.target.value }))
                  }
                  fullWidth
                  size="small"
                />
              </Box>
            ))}
            <Box sx={{ width: 32 }} />
          </Box>

          {/* Virtualized List */}
          <Box sx={{ height: EmpID === "All" ? "65vh" : "30vh"  }}>
            <AutoSizer>
              {({ height, width }) => (
                <List
                  height={height}
                  width={width}
                  itemCount={filteredData.length}
                  itemSize={() => ROW_HEIGHT}
                  itemKey={(index) =>
                    filteredData[index].ValidationLogId || index
                  }
                >
                  {Row}
                </List>
              )}
            </AutoSizer>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ValidationLogList;
